/**
 * One-line descriptions of a distribution.
 *
 * Used by the collapsed variable row and by the printed report, from one
 * function so the two cannot describe the same variable differently — the
 * report is the artefact someone else reviews, and it disagreeing with the
 * editor would be worse than either being terse.
 */

import { bounds, nominal, stdDev, type Distribution } from '../engine/distributions.ts';
import { eng, sig } from './format.ts';
import { isOffsetScale } from './units.ts';

/**
 * Compact enough for a list row: `10.00 kΩ ± 100.0 Ω`.
 *
 * Bounded distributions get a `±` half-range, which is the tolerance an
 * engineer specified. Unbounded ones get `σ` instead — they have no finite
 * bound, and printing the ±3σ clamp as though it were one would state a limit
 * the distribution does not have.
 */
export function describeDistribution(d: Distribution, unit?: string): string {
  if (d.kind === 'fixed') return eng(d.value, 4, unit);

  const b = bounds(d);
  const centre = nominal(d);

  if (b.clamped) return `${eng(centre, 4, unit)} σ ${eng(stdDev(d), 3, unit)}`;

  const half = (b.max - b.min) / 2;
  return `${eng(centre, 4, unit)} ± ${eng(half, 3, unit)}`;
}

/** The half-range as a percentage of nominal, when that is meaningful. */
export function describeTolerancePercent(d: Distribution, unit?: string): string | null {
  if (d.kind === 'fixed') return null;

  // A percentage of a temperature in °C measures the distance from an
  // arbitrary zero, not a tolerance: -40…85 °C would read as "280%".
  if (isOffsetScale(unit)) return null;

  const centre = nominal(d);
  if (!Number.isFinite(centre) || centre === 0) return null;

  const b = bounds(d);
  const half = b.clamped ? 3 * stdDev(d) : (b.max - b.min) / 2;
  if (!(half > 0)) return null;

  return `${sig((half / Math.abs(centre)) * 100, 2)}%${b.clamped ? ' (3σ)' : ''}`;
}

/** `9.900 kΩ … 10.10 kΩ`, or null when the distribution is unbounded. */
export function describeRange(d: Distribution, unit?: string): string | null {
  if (d.kind === 'fixed') return null;
  const b = bounds(d);
  if (b.clamped) return null;
  return `${eng(b.min, 4, unit)} … ${eng(b.max, 4, unit)}`;
}
